import { useEffect, useRef, useState } from 'react';
import type { PdfMetadata } from '../lib/pdfEngine';

interface ExportDialogProps {
  outputCount: number;
  totalPages: number;
  defaultName: string;
  /** The signed-in person, offered as the default author. */
  defaultAuthor: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (baseName: string, metadata: PdfMetadata) => void;
}

export function ExportDialog({
  outputCount,
  totalPages,
  defaultName,
  defaultAuthor,
  busy,
  onCancel,
  onConfirm,
}: ExportDialogProps) {
  const [name, setName] = useState(defaultName);
  const [showProperties, setShowProperties] = useState(true);
  const [title, setTitle] = useState(defaultName);
  /*
   * The title starts as the file name and follows it while they are the same,
   * because that is almost always what is wanted. Once the title is edited it
   * stops following — otherwise renaming the file afterwards would silently
   * discard a title someone had deliberately written.
   */
  const [titleEdited, setTitleEdited] = useState(false);
  const [author, setAuthor] = useState(defaultAuthor);
  const [subject, setSubject] = useState('');
  const [keywords, setKeywords] = useState('');


  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape' && !busy) onCancel();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  const trimmed = name.trim();

  return (
    <div className="dialog-backdrop" onMouseDown={(e) => e.target === e.currentTarget && !busy && onCancel()}>
      <form
        className="dialog"
        onSubmit={(event) => {
          event.preventDefault();
          if (trimmed) {
            onConfirm(trimmed, { title, author, subject, keywords });
          }
        }}
      >
        <h2>Export</h2>
        <p className="dialog__hint">
          {outputCount === 1
            ? `One PDF with ${totalPages} page${totalPages === 1 ? '' : 's'}.`
            : `${outputCount} PDFs (${totalPages} pages total), delivered as a ZIP.`}
        </p>

        <label className="field">
          <span className="field__label">{outputCount === 1 ? 'File name' : 'Base name'}</span>
          <input
            ref={inputRef}
            type="text"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              if (!titleEdited) setTitle(event.target.value);
            }}
            disabled={busy}
            spellCheck={false}
          />
        </label>

        <button
          type="button"
          className="dialog__disclosure"
          aria-expanded={showProperties}
          onClick={() => setShowProperties((v) => !v)}
        >
          {showProperties ? '▾' : '▸'} Document properties
        </button>

        {showProperties && (
          <div className="dialog__properties">
            <p className="dialog__hint" style={{ marginBottom: 12 }}>
              Written into the PDF and shown under “Document Properties” in a reader.
            </p>
            <label className="field">
              <span className="field__label">Title</span>
              <input
                type="text"
                value={title}
                onChange={(event) => {
                  setTitle(event.target.value);
                  setTitleEdited(true);
                }}
                disabled={busy}
                placeholder="Shown in the reader, not the file name"
              />
            </label>
            <label className="field">
              <span className="field__label">Author</span>
              <input
                type="text"
                value={author}
                onChange={(event) => setAuthor(event.target.value)}
                disabled={busy}
              />
            </label>
            <label className="field">
              <span className="field__label">Subject</span>
              <input
                type="text"
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                disabled={busy}
                placeholder="What this document is about"
              />
            </label>
            <label className="field">
              <span className="field__label">Keywords</span>
              <input
                type="text"
                value={keywords}
                onChange={(event) => setKeywords(event.target.value)}
                disabled={busy}
                placeholder="Comma separated"
              />
            </label>
          </div>
        )}

        {outputCount > 1 && (
          <p className="dialog__hint" style={{ margin: 0 }}>
            Files will be named <code>{trimmed || 'document'} 1.pdf</code> through{' '}
            <code>
              {trimmed || 'document'} {outputCount}.pdf
            </code>
            .
          </p>
        )}

        <div className="dialog__actions">
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={busy || !trimmed}>
            {busy ? 'Building…' : 'Download'}
          </button>
        </div>
      </form>
    </div>
  );
}
